import authService from '@/services/auth.service';
import { getWorkosAccessToken, hasWorkosSession } from '@/services/workos-auth-bridge';
import { type InternalAxiosRequestConfig } from 'axios';

/**
 * Attaches the best available auth token to outgoing requests.
 *
 * Resolution order:
 * 1. WorkOS access token (AuthKit-managed, RS256)
 * 2. Legacy localStorage JWT (self-signed HS256)
 * 3. Visitor session token (anonymous sessions, sent via x-session-authorization)
 */
export async function attachAuthToken(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  const workosToken = await getWorkosAccessToken();
  if (workosToken) {
    config.headers['authorization'] = `Bearer ${workosToken}`;
    return config;
  }

  const token = authService.getToken();
  if (token) {
    config.headers['authorization'] = `Bearer ${token}`;
    return config;
  }

  const visitorToken = authService.getVisitorToken();
  if (visitorToken) {
    config.headers['x-session-authorization'] = visitorToken;
  }

  return config;
}

/**
 * Returns true if the current session should handle 401s by redirecting
 * to sign-in. WorkOS sessions should NOT redirect — the AuthKit provider
 * and WorkosAuthBridge component manage the unauthenticated state instead.
 */
export function shouldRedirectOn401(): boolean {
  return !hasWorkosSession();
}
